import React from "react"
import moment from "moment-timezone"
import { Loading } from "@common/EcosuiteComponent"
import { GRAPH_COLORS } from "@common/module/EcosuiteView"
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts"
import DateRangeGraph from "../DateRangeGraph"
import { EnergyGraphTooltip } from "../EnergyGraphTooltip"
import GraphUtils from "@common/utils/GraphUtils"
import i18n from "src/i18n"

const { t } = i18n

// Default loss factors (percentages of the total loss)
// When no loss factors are provided, 100% goes to under performance
const DEFAULT_LOSS_FACTORS = {
  underPerformance: 1.0, // 100% - Default when no other factors provided
}

// Default display names for the chart labels
const DEFAULT_LOSS_FACTOR_DISPLAY_NAMES = {
  ageLoss: "Age Degradation",
  clippingLoss: "Clipping",
  snowLoss: "Snow",
  inverterLoss: "Inverter Efficiency",
  downtimeLoss: "Downtime",
  soilingLoss: "Soiling",
  underPerformanceLoss: "Under Performance",
}

// Static display names that don't change based on loss factors
const STATIC_DISPLAY_NAMES = {
  modeledEnergy: "Modeled Energy",
  measuredEnergy: "Measured Energy",
  totalLoss: "Total Loss",
  originalExpected: "Original Expected (Before PR Adjustment)",
  prAdjustment: "PR Adjustment Amount",
  performanceRatio: "Performance Ratio",
}

/**
 * WaterfallLossGraph component that displays energy generation losses as a stacked bar chart.
 *
 * @class WaterfallLossGraph
 * @extends DateRangeGraph
 *
 * @param {Object} props - Component props
 * @param {Object} [props.lossFactors] - Custom loss factors as percentages (0-1) that should sum to 1.0
 *   Example: { age: 0.05, clipping: 0.1, snow: 0.03, inverter: 0.07, downtime: 0.05, soiling: 0.15, underPerformance: 0.55 }
 * @param {Object} [props.lossFactorDisplayNames] - Custom display names for loss factors
 *   Example: { ageLoss: "Age Degradation", clippingLoss: "Clipping", snowLoss: "Snow" }
 *   Note: Loss factor keys are automatically suffixed with "Loss" for display names
 * @param {Object} props.graphDatums - Actual generation data
 * @param {Object} props.expectedGenerationDatums - Expected generation data
 * @param {Array} props.assets - Array of asset objects containing solar arrays with performance ratios
 * @param {Object} [props.project] - Project object with timezone information
 * @param {string} props.range - Date range for the graph
 * @param {string} props.aggregation - Data aggregation level (e.g., "day", "month")
 */
export default class WaterfallLossGraph extends DateRangeGraph {
  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      showMeasuredEnergy: false,
    }
  }

  // Handle loss factors with the new strategy:
  // - If no factors provided, 100% goes to underPerformance
  // - If factors provided but sum < 100%, underPerformance takes the remainder
  // - If factors sum >= 100%, normalize all factors proportionally
  normalizeLossFactors(lossFactors) {
    const totalFactor = Object.values(lossFactors).reduce((sum, val) => sum + val, 0)

    // If we have factors other than just underPerformance
    const hasOtherFactors = Object.keys(lossFactors).some((key) => key !== "underPerformance")

    if (hasOtherFactors) {
      // Calculate sum of all factors except underPerformance
      const sumWithoutUnderPerformance = Object.keys(lossFactors)
        .filter((key) => key !== "underPerformance")
        .reduce((sum, key) => sum + lossFactors[key], 0)

      if (sumWithoutUnderPerformance < 1.0) {
        // Other factors sum to less than 100%, underPerformance takes the remainder
        const adjustedFactors = { ...lossFactors }
        adjustedFactors.underPerformance = 1.0 - sumWithoutUnderPerformance
        return adjustedFactors
      } else if (sumWithoutUnderPerformance >= 1.0) {
        // Other factors sum to 100% or more, normalize all factors proportionally
        const normalizationFactor = 1.0 / totalFactor
        const normalizedFactors = {}
        Object.keys(lossFactors).forEach((key) => {
          normalizedFactors[key] = lossFactors[key] * normalizationFactor
        })
        return normalizedFactors
      }
    }

    // For the case where only underPerformance is provided or exact sum to 1.0
    if (Math.abs(totalFactor - 1.0) > 1e-6) {
      // Normalize to ensure they sum to exactly 1.0
      const normalizationFactor = 1.0 / totalFactor
      const normalizedFactors = {}
      Object.keys(lossFactors).forEach((key) => {
        normalizedFactors[key] = lossFactors[key] * normalizationFactor
      })
      return normalizedFactors
    }

    return lossFactors
  }

  // Get loss factors from props or use defaults
  getLossFactors() {
    const lossFactors = this.props.lossFactors || DEFAULT_LOSS_FACTORS
    return this.normalizeLossFactors(lossFactors)
  }

  // Get display names combining static names and loss factor names
  getDisplayNames() {
    const lossFactorDisplayNames = this.props.lossFactorDisplayNames || DEFAULT_LOSS_FACTOR_DISPLAY_NAMES
    const lossFactors = this.getLossFactors() // Use normalized factors (includes auto-added underPerformance)

    // Create a flexible mapping that allows display names for both "factor" and "factorLoss" keys
    const flexibleDisplayNames = {}

    // Add all loss factors with flexible lookup
    Object.keys(lossFactors).forEach((factor) => {
      const lossKey = factor + "Loss"

      // Priority order for display name lookup:
      // 1. Exact match for "factorLoss" key
      // 2. Match for original "factor" key
      // 3. Default names for common factors
      // 4. Fallback to capitalized factor name
      flexibleDisplayNames[lossKey] =
        lossFactorDisplayNames[lossKey] ||
        lossFactorDisplayNames[factor] ||
        DEFAULT_LOSS_FACTOR_DISPLAY_NAMES[lossKey] ||
        factor.charAt(0).toUpperCase() + factor.slice(1)
    })

    return {
      ...STATIC_DISPLAY_NAMES,
      ...flexibleDisplayNames,
    }
  }

  // Generate default colors for loss factors
  getBarColors(lossFactors) {
    const defaultColors = {
      measuredEnergy: GRAPH_COLORS[0], // Green for actual production
      ageLoss: "#FFA07A", // Light Salmon
      clippingLoss: "#FF7F50", // Coral
      snowLoss: "#ADD8E6", // Light Blue
      inverterLoss: "#FFD700", // Gold
      downtimeLoss: "#DC143C", // Crimson
      soilingLoss: "#A0522D", // Sienna
      underPerformanceLoss: "#808080", // Grey
    }

    // If custom loss factors are provided, generate colors for any missing ones
    const barColors = { ...defaultColors }
    const colorPalette = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FECA57", "#FF9FF3", "#54A0FF", "#5F27CD"]
    let colorIndex = 0

    Object.keys(lossFactors).forEach((factor) => {
      const lossKey = factor + "Loss"
      if (!barColors[lossKey]) {
        barColors[lossKey] = colorPalette[colorIndex % colorPalette.length]
        colorIndex++
      }
    })

    return barColors
  }

  toggleMeasuredEnergy = () => {
    this.setState((prevState) => ({
      showMeasuredEnergy: !prevState.showMeasuredEnergy,
    }))
  }

  // Calculate the average performance ratio from solar arrays
  calculateAveragePerformanceRatio(assets) {
    if (!assets || assets.length === 0) {
      return 1.0 // Default if no data
    }

    let totalPR = 0
    let totalArrays = 0

    // Iterate through each system/asset
    assets.forEach((system) => {
      if (system.solarArrays && system.solarArrays.length > 0) {
        // Calculate sum of performance ratios for this system
        const systemPR = system.solarArrays.reduce((sum, array) => {
          return sum + (array.performanceRatio || 0)
        }, 0)

        // Add to totals
        totalPR += systemPR
        totalArrays += system.solarArrays.length
      }
    })

    // Return average, or 1.0 if no arrays found
    return totalArrays > 0 ? totalPR / totalArrays : 1.0
  }

  renderContent() {
    const timezone = this.props.project?.timezone ?? this.props.assets?.[0]?.timezone ?? "America/New_York"

    if (!this.props.graphDatums || !this.props.expectedGenerationDatums) {
      // Need both actual and expected generation data
      return <Loading />
    }

    // Get loss factors and display names from props or defaults
    const lossFactors = this.getLossFactors()
    const displayNames = this.getDisplayNames()

    // Calculate the average performance ratio
    const averagePR = this.calculateAveragePerformanceRatio(this.props.assets)

    // Process data for the stacked bar chart
    const data = Object.keys(this.props.graphDatums)
      .map((dateKey) => {
        const actualEntry = this.props.graphDatums[dateKey]
        const expectedEntry = this.props.expectedGenerationDatums[dateKey]

        if (!actualEntry || !expectedEntry) {
          return null // Skip if data is missing for this key
        }

        const date = moment.tz(dateKey, timezone) // Use timezone-aware moment object

        // Adjust expected generation by inverting the performance ratio
        // If PR is 0.82, we divide by 0.82 to get the higher "ideal" value
        const originalExpected = this.round(expectedEntry.expectedGeneration)
        const modeledEnergy = averagePR > 0 ? originalExpected / averagePR : originalExpected

        const measuredEnergy = this.round(actualEntry.generation)
        const totalLoss = Math.max(0, modeledEnergy - measuredEnergy) // Loss cannot be negative

        // ----- DIRECT ASSIGNMENT OF LOSS PORTIONS -----
        // Do NOT round here - just apply the exact proportions
        // Directly calculate each portion based on the exact percentages
        const portions = {}
        let remainingLoss = totalLoss

        // Iterate through all factors except underPerformance to assign exact portions
        Object.keys(lossFactors).forEach((factor) => {
          if (factor !== "underPerformance") {
            const exactPercentage = lossFactors[factor]
            const exactValue = totalLoss * exactPercentage
            portions[factor + "Loss"] = exactValue
            remainingLoss -= exactValue
          }
        })

        // Assign remaining to underPerformance to ensure exact total
        portions.underPerformanceLoss = remainingLoss

        // Check sum to verify
        const sumOfLosses = Object.values(portions).reduce((sum, val) => sum + val, 0)

        // If there's any small floating point discrepancy, adjust underPerformance
        if (Math.abs(sumOfLosses - totalLoss) > 0.0001) {
          portions.underPerformanceLoss += totalLoss - sumOfLosses
        }

        // Create the result object with dynamic loss factors
        const result = {
          dateKey: dateKey,
          date: date.toDate(),
          time: date.toDate().getTime(),
          name: date.format("lll"),
          modeledEnergy: modeledEnergy,
          originalExpected: originalExpected,
          prAdjustment: modeledEnergy - originalExpected,
          measuredEnergy: measuredEnergy,
          totalLoss: totalLoss,
          performanceRatio: averagePR,
        }

        // Add exact values for debugging and display values for each loss factor
        Object.keys(lossFactors).forEach((factor) => {
          const lossKey = factor + "Loss"
          result[`exact${factor.charAt(0).toUpperCase() + factor.slice(1)}Loss`] = portions[lossKey]
          result[lossKey] = portions[lossKey]
        })

        return result
      })
      .filter((entry) => entry !== null) // Remove null entries

    if (data.length === 0) {
      return <div>{t("common.noDataAvailable")}</div> // Handle case with no valid data
    }

    let aggregation = this.props.aggregation
    let graphType = aggregation === "day" ? "linear" : "monotone" // Might adjust later

    // Define bar colors - Choose distinct colors
    const BAR_COLORS = this.getBarColors(lossFactors)

    // Add info about PR adjustment to first data point tooltip
    if (data.length > 0 && averagePR !== 1.0) {
      data[0].prInfo = `PR Adjustment: Expected values scaled by 1/${averagePR.toFixed(2)} = ${(1 / averagePR).toFixed(2)}`
    }

    // Reverse the stacking order to ensure smaller components are visible
    // Order from smallest to largest for better visibility
    // We'll determine order based on the first data point
    const firstDataPoint = data[0]
    const lossComponents = Object.keys(lossFactors).map((factor) => {
      const lossKey = factor + "Loss"
      return {
        key: lossKey,
        value: firstDataPoint?.[lossKey] || 0,
      }
    })

    // Sort components from largest to smallest for better stacking visualization
    // This puts the smaller components at the top of the stack where they'll be more visible
    lossComponents.sort((a, b) => a.value - b.value)

    return (
      <>
        <div style={{ marginBottom: "10px", textAlign: "right" }}>
          <button onClick={this.toggleMeasuredEnergy} className="btn btn-sm btn-outline-primary">
            {this.state.showMeasuredEnergy ? "Hide" : "Show"} Actual Generated
          </button>
        </div>
        <ResponsiveContainer width="100%" height={300} className="ecogy-tooltip">
          <BarChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 10 }} onClick={this.zoomDateRange}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={false} />
            <XAxis
              dataKey="time"
              domain={this.getDomain(this.props.range, this.props.aggregation)}
              tickFormatter={(time) => {
                return GraphUtils.dateFormat(time, this.props.range, this.props.aggregation)
              }}
            />
            <YAxis />
            <Tooltip
              content={
                <EnergyGraphTooltip
                  assets={this.props.assets}
                  getDataTitle={(name) => this.getWaterfallDataTitle(name)}
                  range={this.props.range}
                  aggregation={this.props.aggregation}
                  config={{
                    modeledEnergy: { name: displayNames.modeledEnergy },
                    totalLoss: { name: displayNames.totalLoss },
                    originalExpected: { name: displayNames.originalExpected },
                    prAdjustment: { name: displayNames.prAdjustment },
                    performanceRatio: { name: displayNames.performanceRatio },
                    prInfo: { name: "" }, // Special case for PR info
                  }}
                />
              }
            />
            <Legend iconType="square" />

            {/* Conditionally show Measured Energy as the bottom of the stack */}
            {this.state.showMeasuredEnergy && (
              <Bar
                name={displayNames.measuredEnergy}
                type="monotone"
                dataKey="measuredEnergy"
                stackId="a"
                fill={BAR_COLORS.measuredEnergy || "#2ecc71"}
              />
            )}

            {/* Stacked Bars: Loss Factors */}
            {lossComponents.map((component) => (
              <Bar
                key={component.key}
                name={displayNames[component.key]}
                type={graphType}
                dataKey={component.key}
                stackId="a"
                fill={BAR_COLORS[component.key]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </>
    )
  }

  // Method to get titles for the waterfall chart data keys
  getWaterfallDataTitle = (name) => {
    const displayNames = this.getDisplayNames()
    return displayNames[name] || name // Use the display names or fallback to the key
  }
}

/**
 * Usage Examples:
 *
 * // Basic usage - no loss factors provided, 100% goes to "Under Performance"
 * <WaterfallLossGraph
 *   graphDatums={actualData}
 *   expectedGenerationDatums={expectedData}
 *   assets={assets}
 *   range="month"
 *   aggregation="day"
 * />
 *
 * // Custom loss factors that sum to less than 100%
 * // Under Performance will automatically take the remaining 44%
 * const partialLossFactors = {
 *   age: 0.08,           // 8% age degradation
 *   clipping: 0.12,      // 12% clipping losses
 *   inverter: 0.06,      // 6% inverter losses
 *   soiling: 0.20,       // 20% soiling losses
 *   weather: 0.10,       // 10% weather-related losses
 *   // underPerformance will be automatically set to 0.44 (44%)
 * }
 *
 * // Custom loss factors that sum to exactly 100%
 * const completeLossFactors = {
 *   age: 0.08,           // 8% age degradation
 *   clipping: 0.12,      // 12% clipping losses
 *   inverter: 0.06,      // 6% inverter losses
 *   soiling: 0.20,       // 20% soiling losses
 *   weather: 0.10,       // 10% weather-related losses
 *   underPerformance: 0.44  // 44% other under-performance
 * }
 *
 * const customDisplayNames = {
 *   age: "Panel Degradation",           // Maps to "ageLoss" internally
 *   clipping: "DC Clipping",           // Maps to "clippingLoss" internally
 *   inverter: "Inverter Efficiency",    // Maps to "inverterLoss" internally
 *   soiling: "Dirt & Soiling",         // Maps to "soilingLoss" internally
 *   weather: "Weather Impact",          // Maps to "weatherLoss" internally
 *   underPerformance: "Other Losses"   // Maps to "underPerformanceLoss" internally
 * }
 *
 * // Alternative: You can still use the "Loss" suffix if preferred
 * const alternativeDisplayNames = {
 *   ageLoss: "Panel Degradation",
 *   clippingLoss: "DC Clipping",
 *   inverterLoss: "Inverter Efficiency",
 *   soilingLoss: "Dirt & Soiling",
 *   weatherLoss: "Weather Impact",
 *   underPerformanceLoss: "Other Losses"
 * }
 *
 * <WaterfallLossGraph
 *   graphDatums={actualData}
 *   expectedGenerationDatums={expectedData}
 *   assets={assets}
 *   range="month"
 *   aggregation="day"
 *   lossFactors={partialLossFactors} // or completeLossFactors
 *   lossFactorDisplayNames={customDisplayNames}
 * />
 */
